import React, { useState, useEffect } from 'react';
import { X, Users, Star, Mail, Clock } from 'lucide-react';
import { api } from '../services/api';

/**
 * Reusable post-session analytics view (LLD: single implementation instead
 * of duplicating this panel once for the Mentor Dashboard and again for the
 * Admin Rooms tab). `type` selects which backend endpoint to call.
 */
export default function SessionAnalyticsModal({ sessionId, type = 'peer', onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetcher = type === 'mentor' ? api.getMentorSessionAnalytics : api.getPeerSessionAnalytics;
    fetcher(sessionId)
      .then(setData)
      .catch((e) => setError(e.message || 'Could not load analytics'))
      .finally(() => setLoading(false));
  }, [sessionId, type]);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', overflowY: 'auto' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,.45)' }} onClick={onClose} />
      <div className="fade-in-up" style={{ position: 'relative', background: '#fff', borderRadius: 20, padding: 24, width: '92%', maxWidth: 520, maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 50px rgba(0,0,0,.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--navy-dark)' }}>Session Analytics</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}><X size={20} /></button>
        </div>

        {loading && <p style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: '2rem 0' }}>Loading...</p>}
        {error && <p style={{ color: '#ef4444', fontSize: 13 }}>{error}</p>}

        {data && (
          <>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy-dark)', marginBottom: 4 }}>{data.session.title}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 16, textTransform: 'capitalize' }}>Status: {data.session.status}</div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', gap: 10, marginBottom: 20 }}>
              <div style={{ background: '#f8fafc', borderRadius: 12, padding: '10px 12px', textAlign: 'center' }}>
                <Users size={16} color="#2563eb" style={{ marginBottom: 4 }} />
                <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--navy-dark)' }}>{data.totalAttendees}</div>
                <div style={{ fontSize: 9, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Attendees</div>
              </div>
              <div style={{ background: '#f8fafc', borderRadius: 12, padding: '10px 12px', textAlign: 'center' }}>
                <Star size={16} color="#d97706" style={{ marginBottom: 4 }} />
                <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--navy-dark)' }}>{data.averageRating ? data.averageRating.toFixed(1) : '—'}</div>
                <div style={{ fontSize: 9, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Avg Rating</div>
              </div>
              <div style={{ background: '#f8fafc', borderRadius: 12, padding: '10px 12px', textAlign: 'center' }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--navy-dark)', marginTop: 20 }}>{data.session.enrolled_count}/{data.session.total_capacity}</div>
                <div style={{ fontSize: 9, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Capacity</div>
              </div>
            </div>

            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 10 }}>Attendee List</div>
            {data.attendees.length === 0 ? (
              <p style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>No one has joined yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 240, overflowY: 'auto' }}>
                {data.attendees.map((a) => (
                  <div key={a.userId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: '#f8fafc', borderRadius: 10 }}>
                    <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${a.userId}`} alt="" style={{ width: 28, height: 28, borderRadius: '50%' }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy-dark)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.name || 'Student'}</div>
                      <div style={{ fontSize: 10, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 4 }}><Mail size={9} />{a.email}</div>
                    </div>
                    <div style={{ fontSize: 10, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                      <Clock size={10} />{new Date(a.joinedAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
